import React, { useState, useLayoutEffect, useRef } from 'react';
import { delay } from '../../../utils/CustomHooks';
import ChequeredFlagIcon from '../../../assets/ChequeredFlagIcon';
import { MoneyBackProgressBarProps } from '../../../types';
import {
  ProgressBarWrapper,
  ProgressBarBackground,
  ProgressBarRatio,
  IconWrapper,
} from './MoneyBackProgressBar.styles';

const MoneyBackProgressBar = ({
  daysLeft,
  isExpired,
  isInViewport,
  backgroundColor = '#D9DDE9',
  foregroundColor = '#00AED9',
  customClassName = '',
  customStyles = {},
}: MoneyBackProgressBarProps): JSX.Element => {
  const TOTAL_DAYS = 7;

  /* Take zero day into consideration if it's not expired */
  let zeroDay = 1;

  if (isExpired) {
    zeroDay = 0;
  }

  let remainingDays: number = daysLeft;

  if (daysLeft < 0) {
    remainingDays = 0;
  } else if (daysLeft > 7) {
    remainingDays = 7;
  }

  const [ratio, setRatio] = useState(0);
  const [flagPosition, setFlagPosition] = useState(0);
  const [animationDuration, setAnimationDuration] = useState(1);

  // Using Refs for calculating element width
  const progressBarRef: any = useRef();
  const ratioRef: any = useRef();
  const flagRef: any = useRef();

  const calculateFlagAndProgressPosition = async () => {
    // Set Flag Position
    const progressTotalWidth = progressBarRef.current.offsetWidth;
    const progressSteps = progressTotalWidth / (TOTAL_DAYS + zeroDay);
    setFlagPosition(progressSteps - 20);

    // Delay 500ms before initializing animation
    await delay(500);

    if (isInViewport) {
      let progressWidth = (Math.round((TOTAL_DAYS - remainingDays) * progressSteps) / progressTotalWidth) * 100;
      if (progressWidth > 100) {
        progressWidth = 100;
      } else if (progressWidth < 0) {
        progressWidth = 0;
      }
      setRatio(progressWidth);
      setAnimationDuration(Math.sqrt((progressTotalWidth - progressSteps * remainingDays) / progressSteps) / 2);
    }
  };

  // Reposition Chequered Flag and Progress Width
  useLayoutEffect(() => {
    calculateFlagAndProgressPosition();
    window.addEventListener('resize', calculateFlagAndProgressPosition);
    return () => window.removeEventListener('resize', calculateFlagAndProgressPosition);
  }, [remainingDays]);

  // Progress Styles
  const progressBarBackgroundStyles = {
    backgroundColor,
  };

  const progressBarStyles = {
    backgroundColor: foregroundColor,
    width: `${ratio}%`,
  };

  const flagStyles = {
    right: `${flagPosition}px`,
  };

  return (
    <ProgressBarWrapper className={customClassName} style={customStyles}>
      <ProgressBarBackground ref={progressBarRef} style={progressBarBackgroundStyles}>
        <ProgressBarRatio duration={animationDuration} style={progressBarStyles} ref={ratioRef} />
      </ProgressBarBackground>
      <IconWrapper ref={flagRef} style={flagStyles}>
        <ChequeredFlagIcon />
      </IconWrapper>
    </ProgressBarWrapper>
  );
};

export default MoneyBackProgressBar;
